/**
 * The frame's icons that Paper's icon registry does not have yet
 * (`concierge-desktop/screens/app-frame.md`, Rail): All visits (Material
 * Symbols `face`), Address book (Lucide `notebook-text`) and Space requests
 * (Material Symbols `pending_actions`). Compliance (`shield-check`) and
 * Reception board (`pin`) are Paper's own. The address book's Documents tab
 * adds Lucide's `file-image` and `file` (a document's icon follows its file's
 * type; `file-text` and `file-spreadsheet` are Paper's).
 *
 * `paper-workspace-rail` draws Paper icon names only, so these join Paper's
 * exported `ICON_REGISTRY` once, at the shell. A name Paper already has is
 * never replaced: when Paper ships one of these, its glyph wins and the entry
 * here can go.
 *
 * PAPER-GAP: promote. Ask Paper to add `face`, `notebook-text`,
 * `pending-actions`, `file-image` and `file` to ICON_REGISTRY (or to take app icons through a
 * provider); this file then goes.
 *
 * The markup is a constant from this file, never data.
 */

import { ICON_REGISTRY, type IconDef } from '@ricoh-paper/angular';

/** Material Symbols draw filled on a 960 grid, top edge at -960. */
const MATERIAL = '0 -960 960 960';

export const FRAME_ICONS: Readonly<Record<string, IconDef>> = {
  face: {
    viewBox: MATERIAL,
    content:
      '<path fill="currentColor" stroke="none" d="M324.5-404.5Q310-419 310-440t14.5-35.5Q339-490 360-490t35.5 14.5Q410-461 410-440t-14.5 35.5Q381-390 360-390t-35.5-14.5Zm240 0Q550-419 550-440t14.5-35.5Q579-490 600-490t35.5 14.5Q650-461 650-440t-14.5 35.5Q621-390 600-390t-35.5-14.5ZM480-160q134 0 227-93t93-227q0-24-3-46.5T786-570q-21 5-42 7.5t-44 2.5q-91 0-172-39T390-708q-32 78-91.5 135.5T160-486v6q0 134 93 227t227 93Zm0 80q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Zm-54-715q42 70 114 112.5T700-640q14 0 27-1.5t27-3.5q-42-70-114-112.5T480-800q-14 0-27 1.5t-27 3.5ZM177-581q51-29 89-75t57-103q-51 29-89 75t-57 103Zm249-214Zm-103 36Z"/>',
  },
  'notebook-text': {
    defaultStrokeWidth: 1.75,
    content:
      '<path d="M2 6h4"/><path d="M2 10h4"/><path d="M2 14h4"/><path d="M2 18h4"/><rect width="16" height="20" x="4" y="2" rx="2"/><path d="M9.5 8h5"/><path d="M9.5 12H16"/><path d="M9.5 16H14"/>',
  },
  'pending-actions': {
    viewBox: MATERIAL,
    content:
      '<path fill="currentColor" stroke="none" d="M538.5-138.5Q480-197 480-280t58.5-141.5Q597-480 680-480t141.5 58.5Q880-363 880-280t-58.5 141.5Q763-80 680-80t-141.5-58.5ZM700-288v-92q0-8-6-14t-14-6q-8 0-14 6t-6 14v91q0 8 3 15.5t9 13.5l61 61q6 6 14 6t14-6q6-6 6-14t-6-14l-61-61ZM200-120q-33 0-56.5-23.5T120-200v-560q0-33 23.5-56.5T200-840h167q11-35 43-57.5t70-22.5q40 0 71.5 22.5T594-840h166q33 0 56.5 23.5T840-760v160q0 17-11.5 28.5T800-560q-17 0-28.5-11.5T760-600v-160h-80v80q0 17-11.5 28.5T640-640H320q-17 0-28.5-11.5T280-680v-80h-80v560h180q17 0 28.5 11.5T420-160q0 17-11.5 28.5T380-120H200Zm308.5-651.5Q520-783 520-800t-11.5-28.5Q497-840 480-840t-28.5 11.5Q440-817 440-800t11.5 28.5Q463-760 480-760t28.5-11.5Z"/>',
  },
  'file-image': {
    content:
      '<path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"/><path d="M14 2v4a2 2 0 0 0 2 2h4"/><circle cx="10" cy="12" r="2"/><path d="m20 17-1.296-1.296a2.41 2.41 0 0 0-3.408 0L9 22"/>',
  },
  file: {
    content:
      '<path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"/><path d="M14 2v4a2 2 0 0 0 2 2h4"/>',
  },
};

/** Add the frame's icons to Paper's registry, leaving any name Paper has alone. Idempotent. */
export function registerFrameIcons(): void {
  for (const [name, def] of Object.entries(FRAME_ICONS)) {
    if (!Object.prototype.hasOwnProperty.call(ICON_REGISTRY, name)) ICON_REGISTRY[name] = def;
  }
}
